/**
 * Authoritative inventory quantity formulas and classification helpers.
 *
 * These mirror the single source of truth enforced in the backend:
 * - `on_hand`, `allocated` and `available` are owned by `inventory_ledger`
 *   (append-only), materialised into `inventory_balances` by the
 *   `update_inventory_balances_fn` trigger.
 * - `incoming` is derived from open purchase-order lines
 *   (`purchase_order_items.qty_ordered - qty_received`).
 * - `transferring` is derived from in-transit transfer lines
 *   (`stock_transfer_items.qty_sent - qty_received`).
 *
 * These pure helpers exist so the UI and tests share the exact same rules as
 * the database, rather than reimplementing them ad hoc per screen.
 */

export interface StockQuantities {
  onHand: number;
  allocated: number;
  available: number;
  incoming: number;
  transferring: number;
}

/**
 * Authoritative "available" formula: on hand minus allocated, floored at zero.
 * The database derives `available = on_hand - allocated` with a non-negative
 * CHECK constraint, so the floor here is defensive only.
 */
export function computeAvailable(onHand: number, allocated: number): number {
  return Math.max(onHand - allocated, 0);
}

export type StockStatus = "ok" | "low" | "out";

/**
 * Human, operator-facing label for a stock status. Kept here (not per-screen)
 * so every surface — inventory table, product detail, reports — speaks the same
 * words and so stock state is conveyed by text, not colour alone.
 */
export function stockStatusLabel(status: StockStatus): string {
  switch (status) {
    case "out":
      return "Out of stock";
    case "low":
      return "Low stock";
    default:
      return "In stock";
  }
}

/**
 * Classify a single balance against a reorder point.
 * - "out": no physical stock on hand.
 * - "low": on hand above zero but available at/below the reorder point.
 * - "ok": comfortably above the reorder point.
 */
export function classifyStock(onHand: number, available: number, reorderPoint: number): StockStatus {
  if (onHand <= 0) return "out";
  if (available <= reorderPoint) return "low";
  return "ok";
}

export type ProductStockCell = "untracked" | StockStatus;

/**
 * Classify a product's stock for a list-style surface, keeping the important
 * distinctions between "no stock record yet" and a genuine zero, and between
 * physical zero and fully-allocated (on hand > 0 but nothing sellable).
 * - "untracked": the product has no `inventory_balances` row (opening stock was
 *   never imported / no adjustment yet) — render "Not tracked", NOT "Out of stock".
 * - otherwise delegates to `classifyStock`, which keys "out" on physical
 *   `on_hand <= 0` and "low" on `available <= reorderPoint`. This correctly
 *   treats a fully-allocated product (on hand > 0, available = 0) as "low"
 *   rather than falsely "out", because physical stock still exists.
 */
export function productStockCell(
  hasRecord: boolean,
  onHand: number,
  available: number,
  reorderPoint: number,
): ProductStockCell {
  if (!hasRecord) return "untracked";
  return classifyStock(onHand, available, reorderPoint);
}

export type StockFilter = "all" | "out" | "low" | "incoming" | "allocated" | "available";

/**
 * Predicate for the inventory-screen quick filters.
 * Uses the reorder point (variant-level when present, otherwise product-level)
 * as the "low stock" threshold, consistent with the replenishment plan.
 */
export function matchesStockFilter(q: StockQuantities, filter: StockFilter, reorderPoint: number): boolean {
  switch (filter) {
    case "out":
      return q.onHand <= 0;
    case "low":
      return q.onHand > 0 && q.available <= reorderPoint;
    case "incoming":
      return q.incoming > 0;
    case "allocated":
      return q.allocated > 0;
    case "available":
      return q.available > 0;
    default:
      return true;
  }
}

/**
 * Returns true if applying `delta` to `onHand` would drive it negative.
 * The database rejects such adjustments (and has a non-negative CHECK),
 * so callers use this to validate before submitting.
 */
export function wouldGoNegative(onHand: number, delta: number): boolean {
  return onHand + delta < 0;
}

/** Set-exact delta from authoritative on-hand. Never invent a zero baseline here. */
export function deltaForExactSet(currentOnHand: number, targetQty: number): number {
  return targetQty - currentOnHand;
}

/** Prefer the location that actually holds on-hand so set-exact is not aimed at an empty default. */
export function pickDefaultStockLocation(
  locations: Array<{ id: string; onHand: number }>,
  fallbackId: string | null,
): string | null {
  if (locations.length === 0) return fallbackId;
  const richest = [...locations].sort((a, b) => b.onHand - a.onHand)[0];
  if (richest && richest.onHand > 0) return richest.id;
  return fallbackId ?? locations[0].id;
}

export function simulateAdjustmentSequence(
  startOnHand: number,
  steps: Array<{ kind: "add" | "sub" | "set"; qty: number }>,
): { onHand: number; rejected?: string } {
  let onHand = startOnHand;
  for (const step of steps) {
    const delta = step.kind === "set" ? deltaForExactSet(onHand, step.qty) : step.kind === "add" ? step.qty : -step.qty;
    if (wouldGoNegative(onHand, delta)) {
      return { onHand, rejected: `Adjustment would drive on-hand below zero (currently ${onHand} on hand)` };
    }
    onHand += delta;
  }
  return { onHand };
}

/**
 * Human labels for ledger entry types, expressed in business language rather
 * than raw enum tokens. Kept here (not per-screen) so every movement list —
 * inventory, product detail, location detail, reports — speaks the same words.
 */
export const entryTypeLabel: Record<string, string> = {
  purchase_receipt: "Purchase receipt",
  sale_fulfilment: "Sale",
  transfer_in: "Transfer in",
  transfer_out: "Transfer out",
  return_restock: "Return",
  adjustment: "Adjustment",
  allocation: "Allocation",
  deallocation: "Deallocation",
  opening_balance: "Opening stock",
  reversal: "Reversal",
};

/**
 * Resolve a ledger entry to its business label. A stocktake is posted as an
 * `adjustment` entry with a `stock_count_session` reference, so we surface it
 * as "Stocktake" rather than a generic "Adjustment" to match operator language.
 */
export function movementLabel(entryType: string, referenceType: string | null): string {
  if (referenceType === "stock_count_session") return "Stocktake";
  return entryTypeLabel[entryType] ?? entryType.replace(/_/g, " ");
}